/**
 * TroubleshooterPanel.tsx — AI Troubleshooter slide-over panel (Phase 1 & Phase 2)
 *
 * Phase 1 (Mode A): Diagnosis view
 *   - Severity badge + confidence
 *   - Diagnosis callout
 *   - Keyword gaps (with "Add to Vendor" buttons)
 *   - Keyword conflicts
 *   - Processing note suggestion
 *
 * Phase 2 (Mode B): Audit / Diff view
 *   - Per-line re-resolution with before/after diff table
 *   - Counterfactual simulation (what if keyword X existed?)
 *   - Systemic pattern summary
 *   - Processing note evaluation
 *   - "Promote All Suggestions" → PromoteToVendorDialog batch apply
 *
 * Tab bar added in Phase 2. Mode A remains unchanged.
 * Auto-runs Mode A on mount. Mode B runs when user first clicks its tab.
 */

import React, { useState } from 'react';
import { troubleshootInvoice, reprocessInvoiceAudit, createVendorGLKeyword, createVendorJobKeyword } from '../adminApi';
import type {
  TroubleshooterResponse,
  KeywordGap,
  JobGap,
  KeywordConflict,
  ProcessingNoteSuggestion,
  AuditResponse,
  PromotionItem,
} from '../adminTypes';
import TroubleshooterDiffView from './TroubleshooterDiffView';
import PromoteToVendorDialog from './PromoteToVendorDialog';

// ── Constants ────────────────────────────────────────────────────────────────

const NAVY = '#00386C';

// ── Styles ───────────────────────────────────────────────────────────────────

const overlay: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  width: '480px',
  height: '100vh',
  backgroundColor: 'white',
  boxShadow: '-4px 0 24px rgba(0,0,0,0.18)',
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  fontFamily: 'Arial, sans-serif',
  fontSize: '13px',
};

const panelHeader: React.CSSProperties = {
  backgroundColor: NAVY,
  color: 'white',
  padding: '14px 18px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
};

const panelBody: React.CSSProperties = {
  padding: '18px',
  flex: 1,
  overflowY: 'auto',
};

const sectionHeaderStyle: React.CSSProperties = {
  backgroundColor: NAVY,
  color: 'white',
  padding: '8px 14px',
  fontSize: '12px',
  fontWeight: 'bold',
  letterSpacing: '0.3px',
  borderRadius: '4px 4px 0 0',
  marginBottom: 0,
};

const sectionBodyStyle: React.CSSProperties = {
  border: '1px solid #d0dbe8',
  borderTop: 'none',
  borderRadius: '0 0 4px 4px',
  padding: '14px',
  backgroundColor: 'white',
  marginBottom: '20px',
};

const btnGreen: React.CSSProperties = {
  padding: '5px 11px',
  backgroundColor: '#28a745',
  color: 'white',
  border: 'none',
  borderRadius: '3px',
  cursor: 'pointer',
  fontSize: '11px',
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
};

const btnGreenDisabled: React.CSSProperties = {
  ...btnGreen,
  backgroundColor: '#6c757d',
  cursor: 'not-allowed',
};

const btnClose: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'white',
  fontSize: '20px',
  cursor: 'pointer',
  padding: '0 4px',
  lineHeight: 1,
};

// ── Tab bar styles ────────────────────────────────────────────────────────────

function tabStyle(active: boolean): React.CSSProperties {
  return {
    flex: 1,
    padding: '8px 12px',
    background: active ? 'white' : '#f0f4fb',
    border: 'none',
    borderBottom: active ? '2px solid ' + NAVY : '2px solid transparent',
    color: active ? NAVY : '#6c757d',
    fontWeight: active ? 700 : 400,
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.15s',
  };
}

// ── Severity badge ────────────────────────────────────────────────────────────

const SEVERITY_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  critical: { bg: '#dc3545', text: 'white',   label: 'Critical' },
  warning:  { bg: '#fd7e14', text: 'white',   label: 'Warning'  },
  info:     { bg: '#0d6efd', text: 'white',   label: 'Info'     },
};

function SeverityBadge({ severity }: { severity: string }) {
  const s = SEVERITY_COLORS[severity] || { bg: '#6c757d', text: 'white', label: severity };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 9px',
      backgroundColor: s.bg,
      color: s.text,
      borderRadius: '10px',
      fontSize: '11px',
      fontWeight: 'bold',
      letterSpacing: '0.3px',
    }}>
      {s.label}
    </span>
  );
}

// ── Keyword gap row ────────────────────────────────────────────────────────────

interface GapRowProps {
  gap: KeywordGap;
  vendorNumber: number;
  onAdded: (keyword: string) => void;
}

function GapRow({ gap, vendorNumber, onAdded }: GapRowProps) {
  const [adding, setAdding]   = useState(false);
  const [added, setAdded]     = useState(false);
  const [error, setError]     = useState<string | null>(null);

  async function handleAdd() {
    setAdding(true);
    setError(null);
    try {
      await createVendorGLKeyword({
        vendor_number:  vendorNumber,
        keyword:        gap.suggested_keyword,
        match_operator: 'contains',
        gl_number:      gap.target_gl,
        priority:       100,
        active:         true,
      });
      setAdded(true);
      onAdded(gap.suggested_keyword);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to add keyword');
    } finally {
      setAdding(false);
    }
  }

  return (
    <div style={{
      border: '1px solid #d0dbe8',
      borderRadius: '4px',
      padding: '10px 12px',
      marginBottom: '10px',
      backgroundColor: added ? '#f0fff4' : '#fff',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', fontSize: '12px', color: NAVY, marginBottom: '3px' }}>
            "{gap.suggested_keyword}"
            <span style={{ marginLeft: '8px', color: '#28a745', fontWeight: 'normal' }}>
              → GL {gap.target_gl}
            </span>
            {gap.gl_description && (
              <span style={{ marginLeft: '4px', color: '#555', fontWeight: 'normal', fontSize: '11px' }}>
                ({gap.gl_description})
              </span>
            )}
          </div>
          <div style={{ fontSize: '11px', color: '#555', marginBottom: '2px' }}>
            <strong>Source:</strong> {gap.source_line}
          </div>
          <div style={{ fontSize: '11px', color: '#555', marginBottom: '2px' }}>
            <strong>Why:</strong> {gap.rationale}
          </div>
          <div style={{ fontSize: '11px', color: '#888' }}>
            Confidence: {Math.round(gap.confidence * 100)}%
          </div>
        </div>
        <button
          style={added || adding ? btnGreenDisabled : btnGreen}
          onClick={handleAdd}
          disabled={added || adding}
        >
          {added ? '✓ Added' : adding ? 'Adding…' : 'Add to Vendor'}
        </button>
      </div>
      {error && (
        <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '6px' }}>{error}</div>
      )}
    </div>
  );
}

// ── Job gap row ────────────────────────────────────────────────────────────────

interface JobGapRowProps {
  gap: JobGap;
  vendorNumber: number;
  onAdded: (label: string) => void;
}

function JobGapRow({ gap, vendorNumber, onAdded }: JobGapRowProps) {
  const [adding, setAdding] = useState(false);
  const [added, setAdded]   = useState(false);
  const [error, setError]   = useState<string | null>(null);

  async function handleAdd() {
    setAdding(true);
    setError(null);
    try {
      await createVendorJobKeyword({
        vendor_number:  vendorNumber,
        keyword:        gap.suggested_field,
        keywordvalue:   gap.suggested_value,
        match_operator: (gap.match_operator || 'contains') as 'equals' | 'contains' | 'starts_with' | 'regex',
        job_number:     gap.target_job,
        priority:       100,
        active:         true,
      });
      setAdded(true);
      onAdded(`${gap.suggested_value} → job ${gap.target_job}`);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to add job keyword');
    } finally {
      setAdding(false);
    }
  }

  return (
    <div style={{
      border: '1px solid #c5e0b4',
      borderLeft: '4px solid #70ad47',
      borderRadius: '4px',
      padding: '10px 12px',
      marginBottom: '10px',
      backgroundColor: added ? '#f0fff4' : '#f6fff2',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', fontSize: '12px', color: '#375623', marginBottom: '3px' }}>
            {gap.suggested_field} <em>contains</em> "{gap.suggested_value}"
            <span style={{ marginLeft: '8px', color: '#375623', fontWeight: 'normal' }}>
              → Job {gap.target_job}
            </span>
          </div>
          <div style={{ fontSize: '11px', color: '#555', marginBottom: '2px' }}>
            <strong>Source:</strong> {gap.source_line}
          </div>
          <div style={{ fontSize: '11px', color: '#555', marginBottom: '2px' }}>
            <strong>Why:</strong> {gap.rationale}
          </div>
          <div style={{ fontSize: '11px', color: '#888' }}>
            Confidence: {Math.round(gap.confidence * 100)}%
          </div>
        </div>
        <button
          style={added || adding ? btnGreenDisabled : { ...btnGreen, backgroundColor: '#70ad47' }}
          onClick={handleAdd}
          disabled={added || adding}
        >
          {added ? '✓ Added' : adding ? 'Adding…' : 'Add to Vendor'}
        </button>
      </div>
      {error && (
        <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '6px' }}>{error}</div>
      )}
    </div>
  );
}

// ── Conflict row ──────────────────────────────────────────────────────────────

function ConflictRow({ conflict }: { conflict: KeywordConflict }) {
  return (
    <div style={{
      border: '1px solid #ffc107',
      borderLeft: '4px solid #fd7e14',
      borderRadius: '4px',
      padding: '10px 12px',
      marginBottom: '10px',
      backgroundColor: '#fffdf0',
    }}>
      <div style={{ fontWeight: 'bold', fontSize: '12px', color: '#c55a00', marginBottom: '4px' }}>
        {conflict.conflict_description}
      </div>
      <div style={{ fontSize: '11px', color: '#555', marginBottom: '2px' }}>
        <strong>Keywords:</strong> "{conflict.keyword_a}" vs "{conflict.keyword_b}"
      </div>
      <div style={{ fontSize: '11px', color: '#555', marginBottom: '2px' }}>
        <strong>Affected line:</strong> {conflict.affected_line}
      </div>
      <div style={{ fontSize: '11px', color: '#374151' }}>
        <strong>Fix:</strong> {conflict.recommendation}
      </div>
    </div>
  );
}

// ── Processing note suggestion ────────────────────────────────────────────────

function NoteRow({ suggestion }: { suggestion: ProcessingNoteSuggestion }) {
  return (
    <div style={{
      border: '1px solid #d0dbe8',
      borderRadius: '4px',
      padding: '10px 12px',
      marginBottom: '10px',
    }}>
      <div style={{ marginBottom: '8px' }}>
        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#6c757d', marginBottom: '2px' }}>
          CURRENT NOTE
        </div>
        <div style={{
          fontSize: '12px',
          color: '#374151',
          backgroundColor: '#f8f9fa',
          padding: '6px 8px',
          borderRadius: '3px',
          fontStyle: suggestion.current_note ? 'normal' : 'italic',
        }}>
          {suggestion.current_note || '(none)'}
        </div>
      </div>
      <div style={{ marginBottom: '8px' }}>
        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#28a745', marginBottom: '2px' }}>
          SUGGESTED AMENDMENT
        </div>
        <div style={{
          fontSize: '12px',
          color: '#155724',
          backgroundColor: '#d4edda',
          padding: '6px 8px',
          borderRadius: '3px',
        }}>
          {suggestion.suggested_amendment}
        </div>
      </div>
      <div style={{ fontSize: '11px', color: '#555' }}>
        <strong>Why:</strong> {suggestion.rationale}
      </div>
      <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
        Apply via Vendor Profile → Section 5: AI Processing Notes
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface TroubleshooterPanelProps {
  invoiceTrackingId: string;
  vendorNumber: number;
  vendorName: string;
  onClose: () => void;
}

export default function TroubleshooterPanel({
  invoiceTrackingId,
  vendorNumber,
  vendorName,
  onClose,
}: TroubleshooterPanelProps) {

  // ── User hints (persists across re-runs and mode switches) ─────────────────
  const [userHints, setUserHints]   = useState('');
  const [hintsOpen, setHintsOpen]   = useState(false);

  // ── Mode A (Phase 1 — Diagnosis) state ─────────────────────────────────────
  const [loading, setLoading]     = useState(false);
  const [result, setResult]       = useState<TroubleshooterResponse | null>(null);
  const [error, setError]         = useState<string | null>(null);
  const [addedKws, setAddedKws]   = useState<Set<string>>(new Set());

  // ── Mode B (Phase 2 — Audit) state ─────────────────────────────────────────
  const [activeMode, setActiveMode]           = useState<'A' | 'B'>('A');
  const [auditLoading, setAuditLoading]       = useState(false);
  const [auditResult, setAuditResult]         = useState<AuditResponse | null>(null);
  const [auditError, setAuditError]           = useState<string | null>(null);
  const [promoteItems, setPromoteItems]       = useState<PromotionItem[] | null>(null);
  const [promotedCount, setPromotedCount]     = useState(0);

  // Auto-run Mode A on mount
  React.useEffect(() => {
    runModeA();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Mode A ─────────────────────────────────────────────────────────────────

  async function runModeA() {
    setLoading(true);
    setError(null);
    setResult(null);
    setAddedKws(new Set());
    try {
      const data = await troubleshootInvoice(invoiceTrackingId, userHints.trim() ? { user_hints: userHints.trim() } : undefined);
      setResult(data);
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.message ||
        'Unknown error — try again';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  function handleKeywordAdded(keyword: string) {
    setAddedKws(prev => new Set(prev).add(keyword));
  }

  // ── Mode B ─────────────────────────────────────────────────────────────────

  async function runModeB() {
    setAuditLoading(true);
    setAuditError(null);
    setAuditResult(null);
    setPromotedCount(0);
    try {
      const data = await reprocessInvoiceAudit(invoiceTrackingId, userHints.trim() ? { user_hints: userHints.trim() } : undefined);
      setAuditResult(data);
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.message ||
        'Unknown error — try again';
      setAuditError(msg);
    } finally {
      setAuditLoading(false);
    }
  }

  /** Switch to Mode B and auto-run the audit if not already done. */
  function handleSelectModeB() {
    setActiveMode('B');
    if (!auditResult && !auditLoading && !auditError) {
      runModeB();
    }
  }

  function handlePromoteComplete(count: number) {
    setPromotedCount(count);
    setPromoteItems(null);
  }

  // ── Active meta (for footer) ────────────────────────────────────────────────
  const activeMeta = activeMode === 'A' ? result?._meta : auditResult?._meta;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={overlay}>

      {/* Header */}
      <div style={panelHeader}>
        <div>
          <div style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '2px' }}>
            🔧 AI Troubleshooter
          </div>
          <div style={{ fontSize: '12px', opacity: 0.85 }}>{vendorName}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {activeMode === 'A' && result && !result.no_issues_found && (
            <>
              <SeverityBadge severity={result.severity} />
              <span style={{ fontSize: '12px', opacity: 0.85 }}>
                {Math.round(result.confidence * 100)}% confidence
              </span>
            </>
          )}
          {activeMode === 'B' && auditResult && !auditResult.no_issues_found && (
            <>
              <SeverityBadge severity={auditResult.severity} />
              <span style={{ fontSize: '12px', opacity: 0.85 }}>
                {Math.round(auditResult.confidence * 100)}% confidence
              </span>
            </>
          )}
          <button style={btnClose} onClick={onClose} title="Close">×</button>
        </div>
      </div>

      {/* Mode A / Mode B tab bar */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid #dee2e6',
        backgroundColor: '#f8f9fa',
        flexShrink: 0,
      }}>
        <button style={tabStyle(activeMode === 'A')} onClick={() => setActiveMode('A')}>
          Mode A — Diagnosis
        </button>
        <button style={tabStyle(activeMode === 'B')} onClick={handleSelectModeB}>
          Mode B — Audit
          {auditResult && auditResult.promotion_items?.length > 0 && (
            <span style={{
              marginLeft: '6px',
              backgroundColor: '#dc3545',
              color: 'white',
              borderRadius: '8px',
              padding: '1px 6px',
              fontSize: '10px',
              fontWeight: 700,
            }}>
              {auditResult.promotion_items.length}
            </span>
          )}
        </button>
      </div>

      {/* Context Hints strip */}
      <div style={{ borderBottom: '1px solid #dee2e6', flexShrink: 0, backgroundColor: userHints.trim() ? '#fffbf0' : '#f8f9fa' }}>
        <button
          onClick={() => setHintsOpen(o => !o)}
          style={{
            width: '100%',
            padding: '7px 18px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '12px',
            color: userHints.trim() ? '#856404' : '#6c757d',
            textAlign: 'left',
          }}
        >
          <span style={{ fontSize: '13px' }}>{hintsOpen ? '▾' : '▸'}</span>
          <span style={{ fontWeight: userHints.trim() ? 600 : 400 }}>
            {userHints.trim() ? '💡 Context hints active' : 'Add context hints…'}
          </span>
          {userHints.trim() && !hintsOpen && (
            <span style={{
              marginLeft: '4px',
              color: '#856404',
              fontSize: '11px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '220px',
            }}>
              — {userHints.trim().slice(0, 60)}{userHints.trim().length > 60 ? '…' : ''}
            </span>
          )}
        </button>
        {hintsOpen && (
          <div style={{ padding: '0 18px 12px' }}>
            <textarea
              value={userHints}
              onChange={e => setUserHints(e.target.value)}
              rows={3}
              placeholder={
                'e.g. "Use billing zip code to determine job number."\n' +
                '"Job 3457 = Building A. Job 3458 = Building B."\n' +
                'Free-form — the AI will use this as authoritative context.'
              }
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 10px',
                fontSize: '12px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                resize: 'vertical',
                fontFamily: 'inherit',
                color: '#374151',
                backgroundColor: 'white',
              }}
            />
            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
              Hints are passed to the AI on every run. Click Re-run to apply after editing.
            </div>
          </div>
        )}
      </div>

      {/* Body */}
      <div style={panelBody}>

        {/* ── MODE A ────────────────────────────────────────────────────────── */}
        {activeMode === 'A' && (
          <>
            {/* Loading state */}
            {loading && (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#555' }}>
                <div style={{ fontSize: '28px', marginBottom: '12px' }}>⏳</div>
                <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>Analysing invoice…</div>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  Assembling vendor context and running AI diagnosis.
                  <br />This typically takes 4–10 seconds.
                </div>
              </div>
            )}

            {/* Error state */}
            {error && !loading && (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                padding: '14px 16px',
                marginBottom: '16px',
              }}>
                <div style={{ fontWeight: 'bold', color: '#856404', marginBottom: '6px' }}>
                  Analysis failed
                </div>
                <div style={{ fontSize: '12px', color: '#856404', marginBottom: '10px' }}>{error}</div>
                <button style={btnGreen} onClick={runModeA}>Retry</button>
              </div>
            )}

            {/* Results */}
            {result && !loading && (
              <>
                {/* No-issues state */}
                {result.no_issues_found ? (
                  <div style={{
                    backgroundColor: '#d4edda',
                    border: '1px solid #c3e6cb',
                    borderRadius: '4px',
                    padding: '16px',
                    textAlign: 'center',
                    marginBottom: '20px',
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>✅</div>
                    <div style={{ fontWeight: 'bold', color: '#155724', marginBottom: '4px' }}>
                      No issues found
                    </div>
                    <div style={{ fontSize: '12px', color: '#155724' }}>
                      {result.diagnosis}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Diagnosis callout */}
                    <div style={{
                      backgroundColor: '#e8f4fd',
                      border: '1px solid #b8daff',
                      borderLeft: '4px solid #0d6efd',
                      borderRadius: '4px',
                      padding: '12px 14px',
                      marginBottom: '20px',
                    }}>
                      <div style={{ fontWeight: 'bold', color: '#004085', fontSize: '12px', marginBottom: '4px' }}>
                        DIAGNOSIS
                      </div>
                      <div style={{ color: '#004085', fontSize: '13px', lineHeight: 1.5 }}>
                        {result.diagnosis}
                      </div>
                    </div>

                    {/* Keyword gaps */}
                    {result.keyword_gaps.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <div style={sectionHeaderStyle}>
                          Keyword Gaps ({result.keyword_gaps.length})
                        </div>
                        <div style={{ ...sectionBodyStyle, marginBottom: 0 }}>
                          <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '10px' }}>
                            These line items have no matching keyword — the AI is guessing their GL code.
                            Click "Add to Vendor" to apply the suggestion immediately.
                          </div>
                          {result.keyword_gaps.map((gap, i) => (
                            <GapRow
                              key={i}
                              gap={gap}
                              vendorNumber={vendorNumber}
                              onAdded={handleKeywordAdded}
                            />
                          ))}
                          {addedKws.size > 0 && (
                            <div style={{ fontSize: '11px', color: '#28a745', marginTop: '6px' }}>
                              {addedKws.size} keyword{addedKws.size > 1 ? 's' : ''} added.
                              Re-process this invoice to see the improvement.
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Job gaps */}
                    {result.job_gaps && result.job_gaps.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{ ...sectionHeaderStyle, backgroundColor: '#375623' }}>
                          Job Number Gaps ({result.job_gaps.length})
                        </div>
                        <div style={{ ...sectionBodyStyle, marginBottom: 0, border: '1px solid #c5e0b4', borderTop: 'none' }}>
                          <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '10px' }}>
                            These lines have unresolved job numbers. A matching job helper keyword
                            would assign the correct job automatically.
                            Click "Add to Vendor" to apply immediately.
                          </div>
                          {result.job_gaps.map((gap, i) => (
                            <JobGapRow
                              key={i}
                              gap={gap}
                              vendorNumber={vendorNumber}
                              onAdded={handleKeywordAdded}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Keyword conflicts */}
                    {result.keyword_conflicts.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <div style={sectionHeaderStyle}>
                          Keyword Conflicts ({result.keyword_conflicts.length})
                        </div>
                        <div style={{ ...sectionBodyStyle, marginBottom: 0 }}>
                          <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '10px' }}>
                            Multiple keywords match the same line — the lower-priority one is winning.
                            Edit priorities in the Vendor Profile to resolve.
                          </div>
                          {result.keyword_conflicts.map((c, i) => (
                            <ConflictRow key={i} conflict={c} />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Processing note suggestions */}
                    {result.processing_note_suggestions.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <div style={sectionHeaderStyle}>
                          Processing Note Suggestion
                        </div>
                        <div style={{ ...sectionBodyStyle, marginBottom: 0 }}>
                          {result.processing_note_suggestions.map((s, i) => (
                            <NoteRow key={i} suggestion={s} />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* ── MODE B ────────────────────────────────────────────────────────── */}
        {activeMode === 'B' && (
          <>
            {/* Loading state */}
            {auditLoading && (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#555' }}>
                <div style={{ fontSize: '28px', marginBottom: '12px' }}>⏳</div>
                <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>Running audit…</div>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  Re-resolving each line item and simulating keyword changes.
                  <br />This typically takes 8–15 seconds.
                </div>
              </div>
            )}

            {/* Error state */}
            {auditError && !auditLoading && (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                padding: '14px 16px',
                marginBottom: '16px',
              }}>
                <div style={{ fontWeight: 'bold', color: '#856404', marginBottom: '6px' }}>
                  Audit failed
                </div>
                <div style={{ fontSize: '12px', color: '#856404', marginBottom: '10px' }}>{auditError}</div>
                <button style={btnGreen} onClick={runModeB}>Retry</button>
              </div>
            )}

            {/* Promotion success banner */}
            {promotedCount > 0 && (
              <div style={{
                backgroundColor: '#d4edda',
                border: '1px solid #c3e6cb',
                borderRadius: '4px',
                padding: '10px 14px',
                marginBottom: '12px',
                fontSize: '12px',
                color: '#155724',
                fontWeight: 600,
              }}>
                ✅ {promotedCount} change{promotedCount !== 1 ? 's' : ''} applied to vendor profile.
              </div>
            )}

            {/* Audit diff view */}
            {auditResult && !auditLoading && (
              <TroubleshooterDiffView
                result={auditResult}
                onPromote={setPromoteItems}
              />
            )}
          </>
        )}

        {/* ── Shared meta footer ────────────────────────────────────────────── */}
        {activeMeta && (
          <div style={{
            borderTop: '1px solid #e9ecef',
            paddingTop: '10px',
            marginTop: '8px',
            fontSize: '11px',
            color: '#aaa',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span>
              {activeMeta.model.includes('opus') ? 'Claude Opus' : 'Claude Sonnet'} ·{" "}
              {activeMeta.input_tokens + activeMeta.output_tokens} tokens ·{" "}
              ${activeMeta.cost_usd.toFixed(4)}
            </span>
            <button
              style={{ ...btnGreen, backgroundColor: '#6c757d', fontSize: '11px' }}
              onClick={activeMode === 'A' ? runModeA : runModeB}
            >
              Re-run
            </button>
          </div>
        )}

      </div>

      {/* Promote to Vendor dialog (Mode B) */}
      {promoteItems && (
        <PromoteToVendorDialog
          items={promoteItems}
          vendorNumber={vendorNumber}
          vendorName={vendorName}
          onClose={() => setPromoteItems(null)}
          onComplete={handlePromoteComplete}
        />
      )}

    </div>
  );
}
